import type { ReactNode } from 'react';
import { attributsOnglet, attributsPanneau, clavierOnglets } from './onglets-aria';

/**
 * Barre d'onglets de la plateforme.
 *
 * Reprise telle quelle des vues de travail du répertoire commercial : mêmes
 * classes, même comportement au clavier. Elle est extraite ici parce que le
 * dossier d'une organisation en a besoin aussi, et que deux copies auraient
 * fini par diverger d'un pixel puis d'un comportement.
 *
 * Elle défile horizontalement quand la place manque plutôt que de passer à la
 * ligne : sur mobile, un onglet coupé en deux se lit mal, un onglet qu'on fait
 * glisser se comprend.
 *
 * Onglets et actions occupent **deux rangées distinctes**, jamais un
 * `flex-wrap` commun : avec le wrap, le champ de recherche remontait et
 * redescendait d'une ligne au fil de la frappe — un filtre qui apparaît, une
 * largeur qui change, et la barre saute sous le curseur.
 *
 * Deux rangées même en grand écran : côte à côte, quatre onglets et trois
 * filtres se disputent la largeur et ce sont les onglets qui se comprimaient,
 * jusqu'à défiler alors que la place existait (mesuré à 1440 px). Une ligne
 * gagnée en hauteur vaut mieux qu'un onglet qu'il faut aller chercher.
 */
/**
 * Contenu de l'onglet ouvert, relié à son onglet : un lecteur d'écran annonce
 * « Absences, panneau d'onglet ». `id` est celui passé à la barre
 * (`idPanneau`).
 */
export function PanneauOnglet({
  id,
  actif,
  className,
  children,
}: {
  id: string;
  actif: string;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div {...attributsPanneau(id, actif)} className={className}>
      {children}
    </div>
  );
}

export interface Onglet<T extends string> {
  cle: T;
  libelle: string;
  /** Compteur affiché à droite du libellé. Masqué à zéro. */
  compte?: number;
}

export function Onglets<T extends string>({
  onglets,
  actif,
  onChanger,
  actions,
  etiquette = 'Sections du dossier',
  idPanneau,
}: {
  onglets: readonly Onglet<T>[];
  actif: T;
  onChanger: (cle: T) => void;
  /** Recherche et filtres de la section, alignés à droite de la barre. */
  actions?: ReactNode;
  etiquette?: string;
  /** Identifiant du `PanneauOnglet` qui montre le contenu de l'onglet ouvert. */
  idPanneau?: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-white p-3 shadow-card">
      <div
        role="tablist"
        aria-label={etiquette}
        onKeyDown={clavierOnglets(
          onglets.map((onglet) => onglet.cle),
          actif,
          onChanger,
        )}
        className="relative -mx-1 flex min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto px-1 pb-0.5"
      >
        {onglets.map((onglet, rang) => {
          const selectionne = onglet.cle === actif;
          // Aucun onglet ouvert : le premier reste atteignable au clavier.
          const atteignable = selectionne || (rang === 0 && !onglets.some((autre) => autre.cle === actif));
          return (
            <button
              key={onglet.cle}
              type="button"
              {...attributsOnglet(onglet.cle, selectionne, idPanneau)}
              tabIndex={atteignable ? 0 : -1}
              onClick={() => onChanger(onglet.cle)}
              className={`min-h-[44px] shrink-0 rounded-lg px-3 py-2 text-xs font-bold transition ${
                selectionne
                  ? 'bg-codialis-navy text-white'
                  : 'bg-gray-50 text-codialis-gray hover:bg-gray-100'
              }`}
            >
              {onglet.libelle}
              {onglet.compte !== undefined && onglet.compte > 0 && (
                <span className={selectionne ? ' text-white/70' : ' text-codialis-gray/70'}>
                  {' '}
                  · {onglet.compte}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {actions && (
        <div className="flex flex-wrap items-center gap-2 xl:justify-end">{actions}</div>
      )}
    </div>
  );
}

/** Champ de recherche d'une section, aux dimensions de la barre d'onglets. */
export function ChampRecherche({
  valeur,
  onChange,
  etiquette,
  placeholder,
}: {
  valeur: string;
  onChange: (valeur: string) => void;
  etiquette: string;
  placeholder: string;
}): JSX.Element {
  return (
    <label className="block w-full sm:w-56">
      <span className="sr-only">{etiquette}</span>
      <input
        type="search"
        value={valeur}
        onChange={(evenement) => onChange(evenement.target.value)}
        placeholder={placeholder}
        className="min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-codialis-mint"
      />
    </label>
  );
}

/** Liste déroulante de filtre, aux mêmes dimensions que la recherche. */
export function ChampFiltre({
  valeur,
  onChange,
  etiquette,
  children,
}: {
  valeur: string;
  onChange: (valeur: string) => void;
  etiquette: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="block w-full sm:w-auto">
      <span className="sr-only">{etiquette}</span>
      <select
        value={valeur}
        onChange={(evenement) => onChange(evenement.target.value)}
        className="min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs sm:w-auto"
      >
        {children}
      </select>
    </label>
  );
}
